// Client half of the visitor-session layer: mints/caches a token scoped to
// this visitor's contact and reads the conversation through api/messages.
// The widget NEVER queries helpdesk_messages directly — private data flows
// only through token-authenticated endpoints.

import { ASSET_BASE } from "./asset-base";

const WIDGET_API_BASE = ASSET_BASE;

export interface ApiMessageRow {
  id: string;
  message: string;
  created_at: string;
  sender_type: string;
  media_url: string | null;
  media_type: string | null;
  shopify_products: string | null;
  // Quoted replies: the key of the message this one replies to, and the
  // parent itself when api/messages could resolve it (see api/messages.ts).
  reply_context_id?: string | null;
  reply_to?: ApiQuotedParent | null;
}

export interface ApiQuotedParent {
  id: string;
  message: string;
  sender_type: string;
  media_type: string | null;
}

interface CachedToken {
  key: string; // `${orgId}|${contactId}`
  token: string;
  exp: number;
}

let memToken: CachedToken | null = null;

const lsKey = (organizationId: string) => `flowapt_wtok_${organizationId}`;
const linkKey = (organizationId: string) => `flowapt_cltok_${organizationId}`;

/** Seed a session minted by api/chat-link (WhatsApp deep link). The stored
 *  linkToken lets an expired session re-exchange itself later, KEEPING the
 *  merged WhatsApp+web read scope — a plain widget-session re-mint would
 *  silently downgrade the thread to web-only. */
export function seedWidgetSession(
  organizationId: string,
  contactId: string,
  token: string,
  exp: number,
  linkToken?: string,
): void {
  memToken = { key: `${organizationId}|${contactId}`, token, exp };
  try {
    localStorage.setItem(lsKey(organizationId), JSON.stringify(memToken));
    if (linkToken) localStorage.setItem(linkKey(organizationId), linkToken);
  } catch {
    /* storage unavailable — the in-memory token still carries the session */
  }
}

/** Re-exchange a stored chat-link token for a fresh merged-channel session.
 *  Returns null (and clears a dead link) when unavailable, so the caller
 *  falls back to the ordinary widget-session mint. */
async function mintFromLinkToken(
  organizationId: string,
  contactId: string,
): Promise<CachedToken | null> {
  let linkToken: string | null = null;
  try {
    linkToken = localStorage.getItem(linkKey(organizationId));
  } catch {
    return null;
  }
  if (!linkToken) return null;
  try {
    const res = await fetch(
      `${WIDGET_API_BASE}/api/chat-link?token=${encodeURIComponent(linkToken)}`,
    );
    if (!res.ok) {
      // Expired or revoked — forget it so we stop retrying on every mint.
      try {
        localStorage.removeItem(linkKey(organizationId));
      } catch {
        /* noop */
      }
      return null;
    }
    const data = await res.json();
    if (data?.contactId !== contactId || !data?.sessionToken) return null;
    return {
      key: `${organizationId}|${contactId}`,
      token: data.sessionToken,
      exp: data.sessionExp,
    };
  } catch {
    return null;
  }
}

export async function getWidgetToken(
  organizationId: string,
  contactId: string,
  force = false,
): Promise<string | null> {
  const key = `${organizationId}|${contactId}`;
  const now = Date.now();
  if (!force && memToken?.key === key && memToken.exp - now > 60_000) {
    return memToken.token;
  }
  if (!force) {
    try {
      const raw = localStorage.getItem(lsKey(organizationId));
      if (raw) {
        const cached = JSON.parse(raw) as CachedToken;
        if (cached.key === key && cached.exp - now > 60_000) {
          memToken = cached;
          return cached.token;
        }
      }
    } catch {
      /* storage unavailable — mint fresh */
    }
  }
  // Deep-link sessions renew through their link token first (see above).
  const fromLink = await mintFromLinkToken(organizationId, contactId);
  if (fromLink) {
    memToken = fromLink;
    try {
      localStorage.setItem(lsKey(organizationId), JSON.stringify(memToken));
    } catch {
      /* noop */
    }
    return fromLink.token;
  }
  try {
    const res = await fetch(`${WIDGET_API_BASE}/api/widget-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organizationId, contactId }),
    });
    if (!res.ok) return null;
    const { token, exp } = await res.json();
    if (!token) return null;
    memToken = { key, token, exp };
    try {
      localStorage.setItem(lsKey(organizationId), JSON.stringify(memToken));
    } catch {
      /* noop */
    }
    return token;
  } catch {
    return null;
  }
}

/** Token-authenticated web-session list for the visitor. */
export async function fetchSessions(
  organizationId: string,
  contactId: string,
): Promise<any[] | null> {
  let token = await getWidgetToken(organizationId, contactId);
  if (!token) return null;
  const doFetch = (t: string) =>
    fetch(`${WIDGET_API_BASE}/api/messages?view=sessions`, {
      headers: { Authorization: `Bearer ${t}` },
    });
  try {
    let res = await doFetch(token);
    if (res.status === 401) {
      token = await getWidgetToken(organizationId, contactId, true);
      if (!token) return null;
      res = await doFetch(token);
    }
    if (!res.ok) return null;
    const { sessions } = await res.json();
    return Array.isArray(sessions) ? sessions : [];
  } catch {
    return null;
  }
}

/** Token-authenticated conversation read; retries once on an expired token.
 *  Returns null on failure (callers keep whatever they already have). */
export async function fetchMessages(
  organizationId: string,
  contactId: string,
  opts: { after?: string; order?: "asc" | "desc"; limit?: number } = {},
): Promise<ApiMessageRow[] | null> {
  let token = await getWidgetToken(organizationId, contactId);
  if (!token) return null;

  const qs = new URLSearchParams();
  if (opts.after) qs.set("after", opts.after);
  if (opts.order) qs.set("order", opts.order);
  if (opts.limit) qs.set("limit", String(opts.limit));

  const doFetch = (t: string) =>
    fetch(`${WIDGET_API_BASE}/api/messages?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${t}` },
    });

  try {
    let res = await doFetch(token);
    if (res.status === 401) {
      token = await getWidgetToken(organizationId, contactId, true);
      if (!token) return null;
      res = await doFetch(token);
    }
    if (!res.ok) return null;
    const { messages } = await res.json();
    return Array.isArray(messages) ? messages : [];
  } catch {
    return null;
  }
}
